import { Component, OnInit, signal, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { LogicTransferItem } from './logic-transfer.config';
import { LogicTransferService } from './logic-transfer.service';
import {
  FormField,
  TableSection,
  AddFormComponent,
} from '@shared/components/add-form/add-form.component';
import { TRANSFER_TYPE_OPTIONS } from './logic-transfer.config';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';

@Component({
  selector: 'app-logic-transfer-view',
  standalone: true,
  imports: [
    CommonModule,
    AddFormComponent,
    TranslateModule,
    AppButtonComponent,
  ],
  providers: [MessageService],
  template: `
    @if (loading()) {
      <div class="flex items-center justify-center py-12">
        <div
          data-testid="logic-transfer-view-loading"
          class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
        ></div>
      </div>
    }

    @if (!loading() && formFields().length > 0) {
      <app-add-form
        [formFields]="formFields()"
        [initialData]="formData()"
        [tableSections]="tableSections()"
        [viewMode]="true"
        [title]="'logic_transfer.view.title' | translate"
        (formAction)="onFormAction($event)"
      ></app-add-form>
    }

    @if (!loading() && formFields().length === 0) {
      <div class="flex justify-center items-center h-64">
        <div class="text-center">
          <div class="text-gray-500 text-lg mb-2">
            {{ 'logic_transfer.view.messages.no_data_found' | translate }}
          </div>
          <app-button
            [label]="'common.actions.back' | translate"
            [outlined]="true"
            type="secondary"
            (emitOnClick)="onBackClick()"
          ></app-button>
        </div>
      </div>
    }
  `,
})
export class LogicTransferViewComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly logicTransferService = inject(LogicTransferService);
  private readonly translateService = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();

  // Data signals
  loading = signal(false);
  formFields = signal<FormField[]>([]);
  formData = signal<any>({});
  tableSections = signal<TableSection[]>([]);

  ngOnInit(): void {
    this.loadViewData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Navigation handlers
  onBackClick(): void {
    // Try navigation, fallback to history.back if route fails
    this.router.navigate(['/stock/transfer/logic']).catch(() => {
      window.history.back();
    });
  }

  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'cancel') {
      this.onBackClick();
    }
  }

  private loadViewData(): void {
    this.loading.set(true);

    const id = this.route.snapshot.queryParamMap.get('id');

    if (!id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Invalid view parameters',
      });
      this.router.navigate(['/stock/transfer/logic']);
      return;
    }

    this.loadLogicTransferData(id);
  }

  private loadLogicTransferData(id: string): void {
    this.logicTransferService.getLogicTransferById(id).subscribe({
      next: (data) => {
        if (data) {
          // Load warehouse names if needed
          this.enrichWarehouseNames(data).subscribe({
            next: (enrichedData) => {
              this.configureLogicTransferForm(enrichedData);
              this.configureTableSections(enrichedData);
              this.loading.set(false);
            },
            error: (error) => {
              // Error loading warehouse names
              // Continue without warehouse names
              this.configureLogicTransferForm(data);
              this.configureTableSections(data);
              this.loading.set(false);
            },
          });
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: 'Not Found',
            detail: 'Logic transfer record not found',
          });
          this.router.navigate(['/stock/transfer/logic']);
        }
      },
      error: (error) => {
        // Error loading logic transfer data
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load logic transfer data',
        });
        this.loading.set(false);
        this.router.navigate(['/stock/transfer/logic']);
      },
    });
  }

  private enrichWarehouseNames(
    data: LogicTransferItem,
  ): Observable<LogicTransferItem> {
    const supportId = data.supportLogicWarehouseId;
    const receiveId = data.receivingLogicWarehouseId;

    // If warehouse names already exist, return as is
    if (data.supportWarehouseName && data.receiveWarehouseName) {
      return of(data);
    }

    // Load warehouses from cache/service if not already loaded
    return this.logicTransferService.loadAllWarehouses().pipe(
      map((warehouses) => {
        const enriched = { ...data };

        // Map support warehouse name from cache
        if (supportId && !data.supportWarehouseName) {
          const supportName =
            this.logicTransferService.getWarehouseNameById(supportId);
          if (supportName) {
            enriched.supportWarehouseName = supportName;
          }
        }

        // Map receive warehouse name from cache
        if (receiveId && !data.receiveWarehouseName) {
          const receiveName =
            this.logicTransferService.getWarehouseNameById(receiveId);
          if (receiveName) {
            enriched.receiveWarehouseName = receiveName;
          }
        }

        return enriched;
      }),
      catchError((error) => {
        // Error loading warehouses from cache
        return of(data);
      }),
    );
  }

  private configureLogicTransferForm(data: LogicTransferItem): void {
    // Get transfer type label
    const transferTypeOption = TRANSFER_TYPE_OPTIONS.find(
      (opt) => opt.value === data.transferType,
    );
    const transferTypeLabel = transferTypeOption
      ? this.translateService.instant(transferTypeOption.label as string)
      : data.transferType || '';

    // Get status label - translate it
    const statusLabel = this.getStatusLabel(data.status);

    const fields: FormField[] = [
      {
        label: 'logic_transfer.form.fields.transfer_order_number',
        controlName: 'code',
        type: 'text',
        section: 'logic_transfer.form.sections.basic_information',
        readonly: true,
      },
      {
        label: 'logic_transfer.form.fields.transfer_type',
        controlName: 'transferTypeDisplay',
        type: 'text',
        section: 'logic_transfer.form.sections.basic_information',
        readonly: true,
      },
      {
        label: 'logic_transfer.form.fields.description',
        controlName: 'description',
        type: 'text',
        section: 'logic_transfer.form.sections.basic_information',
        readonly: true,
      },
      {
        label: 'logic_transfer.form.fields.source_warehouse',
        controlName: 'supportWarehouseName',
        type: 'text',
        section: 'logic_transfer.form.sections.basic_information',
        readonly: true,
      },
      {
        label: 'logic_transfer.form.fields.destination_warehouse',
        controlName: 'receiveWarehouseName',
        type: 'text',
        section: 'logic_transfer.form.sections.basic_information',
        readonly: true,
      },
      {
        label: 'logic_transfer.table.columns.status',
        controlName: 'statusDisplay',
        type: 'text',
        section: 'logic_transfer.form.sections.basic_information',
        readonly: true,
      },
      {
        label: 'logic_transfer.form.fields.expected_receive_date',
        controlName: 'expectedReceiveDate',
        type: 'text',
        section: 'logic_transfer.form.sections.basic_information',
        readonly: true,
      },
    ];

    // Prepare form data with display values
    const formData = {
      ...data,
      transferTypeDisplay: transferTypeLabel,
      statusDisplay: statusLabel,
      expectedReceiveDate: this.formatDate(
        data.expectedReceiveDate || data.plannedDeliveryDate,
      ),
    };

    this.formFields.set(fields);

    // Use setTimeout to ensure form initialization happens after formFields
    setTimeout(() => {
      this.formData.set(formData);
    }, 0);
  }

  private configureTableSections(data: LogicTransferItem): void {
    const tableData = (data.items || []).map((item: any, index: number) => {
      const mapped = {
        skuCode: item.skuCode || '',
        skuName: item.skuName || '',
        spuCode: item.spuCode || '',
        spuName: item.spuName || '',
        quantity: item.plannedQty || 0,
      };
      return mapped;
    });

    const tableSection: TableSection = {
      title: 'logic_transfer.form.sections.product_details',
      show: true,
      hasCheckbox: false,
      hasSerialColumn: true,
      data: tableData,
      columns: [
        {
          prop: 'spuCode',
          label: 'logic_transfer.form.table.columns.spu_code',
          width: 150,
          type: 'text',
          align: 'left',
        },
        {
          prop: 'spuName',
          label: 'logic_transfer.form.table.columns.spu_name',
          width: 200,
          type: 'text',
          align: 'left',
        },
        {
          prop: 'skuCode',
          label: 'logic_transfer.form.table.columns.sku_code',
          width: 150,
          type: 'text',
          align: 'left',
        },
        {
          prop: 'skuName',
          label: 'logic_transfer.form.table.columns.sku_name',
          width: 200,
          type: 'text',
          align: 'left',
        },
        {
          prop: 'quantity',
          label: 'logic_transfer.form.table.columns.quantity',
          width: 150,
          type: 'text',
          align: 'right',
          useGrouping: true,
        },
      ],
      actions: [],
    };

    this.tableSections.set([tableSection]);
  }

  private getStatusLabel(status: string): string {
    const statusMap: { [key: string]: string } = {
      PENDING: 'logic_transfer.status.pending',
      APPROVED: 'logic_transfer.status.approved',
      IN_PROGRESS: 'logic_transfer.status.in_progress',
      COMPLETED: 'logic_transfer.status.completed',
      CANCELLED: 'logic_transfer.status.cancelled',
    };
    const statusKey = (status || '').toUpperCase();
    const translationKey = statusMap[statusKey] || status;
    // Translate the key to actual text
    return this.translateService.instant(translationKey);
  }

  private formatDate(dateString?: string): string {
    if (!dateString) return '';
    try {
      // Parse date string and extract date part only (YYYY-MM-DD) to avoid timezone issues
      // Handle both ISO format (2024-12-24T00:00:00) and date-only format (2024-12-24)
      const datePart = dateString.split('T')[0]; // Get YYYY-MM-DD part
      const [year, month, day] = datePart.split('-');

      if (year && month && day) {
        // Format as DD/MM/YYYY (Vietnamese format)
        return `${day}/${month}/${year}`;
      }

      // Fallback to Date object parsing if format is different
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }

      return dateString;
    } catch {
      return dateString;
    }
  }
}
